import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  CircularProgress,
  Tooltip
} from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { AutoBuyParameter, FilterType } from '../types';
import RangeInput from './RangeInput';
import FilterSelect from './FilterSelect';
import BasicModalConfirmation from '../../../../../../../../../components/basicModalConfirmation';

// Dropdown options for filter types
const filterOptions = [
  { value: FilterType.EXACT, label: 'Exact' },
  { value: FilterType.RANGE, label: 'Range' },
  { value: FilterType.CONTAINS, label: 'Contain' },
  { value: FilterType.DISABLE, label: 'Disable' }
];

interface ParameterRowProps {
  param: AutoBuyParameter;
  errors?: { [field: string]: string };  // Object with errors for each field
  onTicketsGoalChange: (tempId: string, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onLimitedViewChange: (tempId: string, e: React.ChangeEvent<HTMLInputElement>) => void;
  onParameterChange: (tempId: string, field: keyof AutoBuyParameter, value: any) => void;
  onPriceRangeChange: (tempId: string, min: number, max: number) => void;
  onDeleteParameter: (tempId: string) => Promise<void>;
  extractRangeValues: (value: string) => { min: number, max: number }; // For numeric fields
  extractAlphanumericRangeValues?: (value: string) => { min: string, max: string }; // For alphanumeric fields
  isDeletingParam?: string | null; // ID of parameter currently being deleted
}

/**
 * Component for rendering a single parameter row
 */
const ParameterRow: React.FC<ParameterRowProps> = ({
  param,
  errors = {},
  onTicketsGoalChange,
  onLimitedViewChange,
  onParameterChange,
  onPriceRangeChange,
  onDeleteParameter,
  extractRangeValues,
  extractAlphanumericRangeValues = (v) => ({ min: '', max: '' }), // Default fallback
  isDeletingParam
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  return (
    <Box key={param.tempId} sx={{ bgcolor: '#f9f9f9', p: 1, borderRadius: 1 }}>
      {/* Row with Tickets Goal and Limited View */}
      <Box sx={{ mb: 2, mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography sx={{ fontWeight: '600', fontSize: '14px', width: 'auto', lineHeight: '32px', mr: 1 }}>
          Tickets goal:
        </Typography>
        <Tooltip 
          title={errors.ticketsGoal || ''}
          open={!!errors.ticketsGoal}
          placement="top"
          arrow
          sx={{ 
            '& .MuiTooltip-tooltip': { 
              backgroundColor: '#f44336', 
              color: 'white',
              fontSize: '0.875rem',
              padding: '8px 12px',
              borderRadius: '4px',
              maxWidth: '300px'
            } 
          }}
        >
          <TextField 
            sx={{ width: '75px' }}
            type="text" 
            size="small"
            value={param.ticketsGoal}
            onChange={(e) => onTicketsGoalChange(param.tempId, e)}
            error={!!errors.ticketsGoal}
            // We don't use helperText to avoid the space issue
            // helperText={errors.ticketsGoal}
          />
        </Tooltip>
        <Box sx={{ ml: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={param.limitedView || false}
                onChange={(e) => onLimitedViewChange(param.tempId, e)}
                size="small"
              />
            }
            label="Limited View"
          />
        </Box>
        {/* General Admission checkbox was removed */}
      </Box>
      
      {/* Row with parameters */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        {/* Quantity */}
        <Box sx={{ width: '25%' }}>
          <Typography sx={{ fontWeight: '600', fontSize: '14px', mb: 1 }}>Quantity:</Typography>
          <FilterSelect
            value={param.quantityType}
            onChange={(e) => onParameterChange(param.tempId, 'quantityType', e.target.value)}
            options={filterOptions}
            filterDisableOption={true}
          />
          {param.quantityType === FilterType.RANGE ? (
            <RangeInput
              value={param.quantity}
              onRangeChange={(min, max) => {
                onParameterChange(param.tempId, 'quantity', `${min},${max}`);
              }}
              extractRangeValues={extractRangeValues}
              error={!!errors.quantity}
              helperText={errors.quantity}
            />
          ) : (
            <TextField
              fullWidth
              size="small"
              value={param.quantity}
              onChange={(e) => onParameterChange(param.tempId, 'quantity', e.target.value)}
              sx={{ mt: 1 }}
              error={!!errors.quantity}
              helperText={errors.quantity}
              disabled={param.quantityType === FilterType.DISABLE}
            />
          )}
        </Box>

        {/* Section */}
        <Box sx={{ width: '25%' }}>
          <Typography sx={{ fontWeight: '600', fontSize: '14px', mb: 1 }}>Section:</Typography>
          <FilterSelect
            value={param.sectionType}
            onChange={(e) => onParameterChange(param.tempId, 'sectionType', e.target.value)}
            options={filterOptions}
          />
          {param.sectionType === FilterType.RANGE ? (
            <RangeInput
              value={param.section}
              onRangeChange={(min, max) => {
                onParameterChange(param.tempId, 'section', `${min},${max}`);
              }}
              extractRangeValues={extractAlphanumericRangeValues}
              error={!!errors.section}
              helperText={errors.section}
            />
          ) : (
            <TextField
              fullWidth
              size="small"
              value={param.section}
              onChange={(e) => onParameterChange(param.tempId, 'section', e.target.value)}
              sx={{ mt: 1 }}
              error={!!errors.section}
              helperText={errors.section}
              disabled={param.sectionType === FilterType.DISABLE}
            />
          )}
        </Box>

        {/* Row */}
        <Box sx={{ width: '25%' }}>
          <Typography sx={{ fontWeight: '600', fontSize: '14px', mb: 1 }}>Row:</Typography>
          <FilterSelect
            value={param.rowType}
            onChange={(e) => onParameterChange(param.tempId, 'rowType', e.target.value)}
            options={filterOptions}
          />
          {param.rowType === FilterType.RANGE ? (
            <RangeInput
              value={param.row}
              onRangeChange={(min, max) => {
                onParameterChange(param.tempId, 'row', `${min},${max}`);
              }}
              extractRangeValues={extractAlphanumericRangeValues}
              error={!!errors.row}
              helperText={errors.row}
            />
          ) : (
            <TextField
              fullWidth
              size="small"
              value={param.row}
              onChange={(e) => onParameterChange(param.tempId, 'row', e.target.value)}
              sx={{ mt: 1 }}
              error={!!errors.row}
              helperText={errors.row}
              disabled={param.rowType === FilterType.DISABLE}
            />
          )}
        </Box>

        {/* Price Points */}
        <Box sx={{ width: '25%' }}>
          <Typography sx={{ fontWeight: '600', fontSize: '14px', mb: 1 }}>Price point:</Typography>
          <FilterSelect
            value={param.pricePointType}
            onChange={(e) => onParameterChange(param.tempId, 'pricePointType', e.target.value)}
            options={filterOptions}
            filterDisableOption={true}
          />
          {param.pricePointType === FilterType.RANGE ? (
            <RangeInput
              value={param.pricePoint}
              onRangeChange={(min, max) => {
                console.log('Price range changing to:', min, max);
                onPriceRangeChange(param.tempId, Number(min), Number(max));
              }}
              extractRangeValues={extractRangeValues}
              error={!!errors.pricePoint}
              helperText={errors.pricePoint}
              minPlaceholder="Min $"
              maxPlaceholder="Max $"
            />
          ) : (
            <TextField
              fullWidth
              size="small"
              value={param.pricePoint}
              onChange={(e) => onParameterChange(param.tempId, 'pricePoint', e.target.value)}
              sx={{ mt: 1 }}
              error={!!errors.pricePoint}
              helperText={errors.pricePoint}
              disabled={param.pricePointType === FilterType.DISABLE}
            />
          )}
        </Box>

        {/* Delete button with confirmation */}
        <React.Fragment>
          {isDeletingParam === param.tempId ? (
            <Box sx={{ mt: 3.5, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} color="error" />
            </Box>
          ) : (
            <Tooltip title="Delete rule">
              <span>
                <IconButton 
                  color="error" 
                  onClick={() => setShowConfirmation(true)}
                  sx={{ mt: 3.5 }}
                >
                  <DeleteOutlineOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          
          {/* Confirmation Modal */}
          <BasicModalConfirmation 
            open={showConfirmation}
            handleClose={() => setShowConfirmation(false)}
            title={
              `Are you sure you want to delete this rule?\n\n
              Tickets Goal: ${param.ticketsGoal}\n
              Quantity: ${param.quantity || 'Not specified'}\n
              Section: ${param.section || 'Not specified'}\n
              Row: ${param.row || 'Not specified'}`
            }
            confirmLabel="Delete"
            cancelLabel="Cancel"
            onClick={async () => {
              setShowConfirmation(false);
              await onDeleteParameter(param.tempId);
            }}
          />
        </React.Fragment>
      </Box>
    </Box>
  );
};

export default ParameterRow;
